<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.country, 'Țară')"
      :items="countries"
      item-title="name"
      name="country"
      item-value="id"
      v-model="selectedCountry"
      :loading="loading"
      :disabled="loading"
      :rules="[v => !!v || translation(translationList.required, 'Acest câmp este obligatoriu')]"
      return-object
  >
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch } from 'vue';
import { useAddressStore } from '~/plugins/store/address';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type { Country } from '~/types/Address';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => Country | null,
      default: () => null,
      required: false
    },
    continentCode: {
      type: String as () => string | null | undefined,
      required: false,
      default: null
    }
  },
  emits: ['update:modelValue', 'validation-address-status'],
  setup(props, { emit }) {
    const { modelValue, continentCode } = toRefs(props);
    const addressStore = useAddressStore();
    const translationStore = useTranslationStore();
    const selectedCountry = ref(modelValue.value ? modelValue.value : null);
    const countries = ref<Country[]>([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    let initialSetupDone = false;
    const error = ref([]);

    const fetchCountries = async (continentCodeValue?: string) => {
      loading.value = true;
      if (continentCodeValue) {
        countries.value = await addressStore.getCountriesByContinent(continentCodeValue, ['ro']);
      } else {
        countries.value = await addressStore.getCountries();
      }
      loading.value = false;
    }

    onMounted(async () => {
      await fetchCountries(continentCode.value ? continentCode.value : undefined);
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      initialSetupDone = true;
      const isValid = !!selectedCountry.value;
      emit('validation-address-status', { field: 'country', isValid });

      window.addEventListener('validate-address', handleValidationEvent);
      window.addEventListener('validate-travel', handleValidationEvent);
    });

    const handleValidationEvent = (data: any) => {
      if (data.detail && data.detail === 'country') {
        if (!selectedCountry.value) {
          error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        } else {
          error.value = [];
          emit('validation-address-status', { field: 'country', isValid: true });
        }
      }
    };

    watch(continentCode, async (newContinentCode) => {
      if (modelValue.value && modelValue.value.continent_code === newContinentCode) {
        selectedCountry.value = modelValue.value;
      } else {
        selectedCountry.value = null;
        emit('update:modelValue', null);
      }
      countries.value = [];
      if (newContinentCode) {
        await fetchCountries(newContinentCode);
      }
    });

    watch(selectedCountry, (newCountry) => {
      if (initialSetupDone) {
        emit('update:modelValue', newCountry);
      }
    });

    watch(modelValue, (newValue) => {
      error.value = [];
      if (newValue && (newValue.id !== selectedCountry.value?.id)) {
        selectedCountry.value = newValue;
      }
      const isValid = !!newValue;
      emit('validation-address-status', { field: 'country', isValid });
    });

    return {
      selectedCountry,
      translationList,
      translation,
      countries,
      error,
      loading,
    };
  },
});
</script>

<style scoped>
</style>
